import React, { useEffect, useRef } from 'react';
import { Row, Col, Button } from 'antd';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Loader from '../../components/loader/Loader';
import paymentSuccess from '../../assets/payment-success.svg';
import paymentFailed from '../../assets/payment-failed.svg';
import { verifyMerchantCheckoutSession } from '../../redux/merchantRegistration/actions';
import { updateUserOnboardingCurrentStep } from '../../redux/authentication/actions';

export default function MerchantPaymentStatusPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  const loaderStatus = useSelector((state) => state?.loader?.transparentLoader);
  const verifyCheckoutSessionSuccess = useSelector(
    (state) => state?.merchantRegistration?.verifyCheckoutSessionSuccess,
  );
  const verifyCheckoutSessionLoading = useSelector(
    (state) => state?.merchantRegistration?.verifyCheckoutSessionLoading,
  );
  const verifyCheckoutSessionError = useSelector((state) => state?.merchantRegistration?.verifyCheckoutSessionError);
  const language = useSelector((state) => state?.auth?.language);

  useEffect(() => {
    if (sessionId) {
      dispatch(verifyMerchantCheckoutSession(sessionId));
    }
  }, [dispatch, sessionId]);

  const verificationDone =
    !verifyCheckoutSessionLoading && (verifyCheckoutSessionSuccess !== null || verifyCheckoutSessionError);
  const isSuccess = verifyCheckoutSessionSuccess === true;
  const navigatedAfterSuccessRef = useRef(false);
  const failureMessage =
    typeof verifyCheckoutSessionError === 'string'
      ? verifyCheckoutSessionError
      : verifyCheckoutSessionError?.message ||
        language?.content?.PAYMENT_FAIL ||
        'Payment could not be confirmed. You can try again or continue to your profile.';

  useEffect(() => {
    if (!verificationDone || !isSuccess || navigatedAfterSuccessRef.current) return;
    navigatedAfterSuccessRef.current = true;
    dispatch(updateUserOnboardingCurrentStep(6));
    const t = setTimeout(() => {
      navigate('/profile', { replace: true });
    }, 1600);
    return () => clearTimeout(t);
  }, [verificationDone, isSuccess, dispatch, navigate]);

  const handleBackToProfile = () => {
    dispatch(updateUserOnboardingCurrentStep(6));
  };

  if (!sessionId) {
    return (
      <div className="merchant-otp-container">
        <div className="ninjadash-authentication-content text-center">
          <div className="color-danger payment-status-text">
            {language?.content?.INVALID_RETURN_MISSING_SESSION || 'Invalid return. Missing session.'}
          </div>
          <div className="pt-3">
            <Link
              to="/profile"
              onClick={handleBackToProfile}
              style={{ fontSize: '16px', color: '#007DFC', fontWeight: '600' }}
            >
              {language?.content?.BACK_TO_HOME || 'Back to home'}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {loaderStatus && <Loader />}
      <div className="merchant-otp-container" style={{ border: 'none' }}>
        {verificationDone && (
          <Row gutter={[16, 16]}>
            <Col xs={24} className="text-center">
              <div className="mx-auto">
                {isSuccess ? (
                  <>
                    <img src={paymentSuccess} alt="payment success" className="img-fluid" />
                    <div className="color-success payment-status-text">
                      {language?.content?.PAYMENT_SUCCESS || 'Payment successful'}
                    </div>
                  </>
                ) : (
                  <>
                    <img src={paymentFailed} alt="payment failed" className="img-fluid" />
                    <div className="color-danger payment-status-text">{failureMessage}</div>
                  </>
                )}
              </div>
              <div className="pt-3" style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center' }}>
                {!isSuccess && (
                  <Link to="/onboarding/payment">
                    <Button type="primary" size="large">
                      {language?.content?.TRY_PAYMENT_AGAIN || 'Try payment again'}
                    </Button>
                  </Link>
                )}
                <Link
                  to="/profile"
                  onClick={handleBackToProfile}
                  style={{ fontSize: '16px', color: '#007DFC', fontWeight: '600' }}
                >
                  {language?.content?.BACK_TO_HOME || 'Back to profile'}
                </Link>
              </div>
            </Col>
          </Row>
        )}
        {!verificationDone && !loaderStatus && (
          <div className="ninjadash-authentication-content text-center">
            <div className="payment-status-text">{language?.content?.VERIFYING_PAYMENT || 'Verifying payment...'}</div>
          </div>
        )}
      </div>
    </>
  );
}
